import { useEffect, useState } from 'react';

/** True if `marker` is fully inside `root`. */
function useIsMarkerVisible(root: HTMLElement | null, marker: HTMLElement | null) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!root || !marker) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          setIsVisible(entry.isIntersecting && entry.intersectionRatio >= 1);
        }
      },
      { root, threshold: 1 }
    );
    observer.observe(marker);

    return () => observer.disconnect();
  }, [root, marker]);

  return isVisible;
}

/** Tracks scroll edge markers, not offsets. */
export function useTermsScrollState() {
  const [scrollRoot, setScrollRoot] = useState<HTMLElement | null>(null);
  const [startMarker, setStartMarker] = useState<HTMLElement | null>(null);
  const [endMarker, setEndMarker] = useState<HTMLElement | null>(null);

  const isStartVisible = useIsMarkerVisible(scrollRoot, startMarker);
  const isEndVisible = useIsMarkerVisible(scrollRoot, endMarker);

  // Latch when end is reached, don't retract
  const [hasReachedEnd, setHasReachedEnd] = useState(false);
  useEffect(() => {
    if (isEndVisible) setHasReachedEnd(true);
  }, [isEndVisible]);

  return {
    hasReachedEnd,
    hasContentAbove: !isStartVisible,
    hasContentBelow: !isEndVisible,
    setScrollRoot,
    setStartMarker,
    setEndMarker
  };
}
